import { useEffect, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useCommandPalette } from "@/hooks/use-command-palette";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Building2,
  Truck,
  Phone,
  FileText,
  Plus,
  LayoutDashboard,
  Receipt,
  CreditCard,
  Layers,
  AlertTriangle,
  Eye,
  FolderOpen,
} from "lucide-react";
import type { Company, CarrierWithRelations } from "@shared/schema";

type CompanyWithDotProfile = Company & {
  dotProfile?: { dotNumber?: string | null } | null;
};

interface ContactWithCompanies {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  companies?: Array<{
    company: {
      id: number;
      name: string;
    };
  }>;
}

interface WorkOrderListItem {
  id: number;
  workOrderNumber: string | null;
  carrierId: number | null;
  carrierName: string | null;
  companyName: string | null;
  serviceType: string | null;
}

interface CommandPaletteProps {
  onCreateCompany?: () => void;
  onCreateCarrier?: () => void;
  onCreateContact?: () => void;
  onCreateWorkOrder?: () => void;
}

const NAV_ITEMS = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/dashboard" },
  { label: "Work Orders", icon: FileText, href: "/work-orders" },
  { label: "Companies", icon: Building2, href: "/companies" },
  { label: "Carriers", icon: Truck, href: "/carriers" },
  { label: "Contacts", icon: Phone, href: "/contacts" },
  { label: "Invoices", icon: Receipt, href: "/invoices" },
  { label: "Payments", icon: CreditCard, href: "/payments" },
  { label: "Batch Invoicing", icon: Layers, href: "/batch-invoices" },
  { label: "Authority Letters", icon: FileText, href: "/authority-letters" },
  { label: "Watchlists", icon: Eye, href: "/watchlists" },
  { label: "FMCSA Incidents", icon: AlertTriangle, href: "/fmcsa-incidents" },
  { label: "Files", icon: FolderOpen, href: "/documents" },
] as const;

export function CommandPalette({
  onCreateCompany,
  onCreateCarrier,
  onCreateContact,
  onCreateWorkOrder,
}: CommandPaletteProps) {
  const { open, setOpen } = useCommandPalette();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const { data: companies = [] } = useQuery<CompanyWithDotProfile[]>({
    queryKey: ["/api/companies", { search: debouncedSearch }],
    queryFn: async () => {
      const url = debouncedSearch.trim()
        ? `/api/companies?search=${encodeURIComponent(debouncedSearch.trim())}`
        : '/api/companies';
      const res = await fetch(url, { credentials: 'include' });
      return res.json();
    },
    enabled: open,
    staleTime: 30000,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: carriers = [] } = useQuery<CarrierWithRelations[]>({
    queryKey: ["/api/carriers", "command-palette", { search: debouncedSearch }],
    queryFn: async () => {
      const url = debouncedSearch.trim()
        ? `/api/carriers?search=${encodeURIComponent(debouncedSearch.trim())}`
        : '/api/carriers';
      const res = await fetch(url, { credentials: 'include' });
      return res.json();
    },
    enabled: open,
    staleTime: 30000,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: contacts = [] } = useQuery<ContactWithCompanies[]>({
    queryKey: ["/api/contacts", "command-palette", "with-companies", { search: debouncedSearch }],
    queryFn: async () => {
      const url = debouncedSearch.trim()
        ? `/api/contacts?include=companies&search=${encodeURIComponent(debouncedSearch.trim())}`
        : '/api/contacts?include=companies';
      const res = await apiRequest("GET", url);
      const body = await res.json();
      return body;
    },
    enabled: open,
    staleTime: 30000,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: workOrders = [] } = useQuery<WorkOrderListItem[]>({
    queryKey: ["/api/work-orders/list", { search: debouncedSearch }],
    queryFn: async () => {
      const url = debouncedSearch.trim()
        ? `/api/work-orders/list?search=${encodeURIComponent(debouncedSearch.trim())}`
        : '/api/work-orders/list';
      const res = await fetch(url, { credentials: 'include' });
      return res.json();
    },
    enabled: open,
    staleTime: 30000,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const q = debouncedSearch.trim();

  const filteredCompanies = q ? companies : companies.slice(0, 5);
  const filteredCarriers = q ? carriers : carriers.slice(0, 5);
  const filteredContacts = q ? contacts : contacts.slice(0, 5);
  const filteredWorkOrders = q ? workOrders : workOrders.slice(0, 5);

  const filteredNav = q
    ? NAV_ITEMS.filter((n) => n.label.toLowerCase().includes(q.toLowerCase()))
    : [];

  const navigate = (path: string) => {
    setLocation(path);
    setOpen(false);
  };

  const runAction = (fn?: () => void) => {
    setOpen(false);
    setTimeout(() => fn?.(), 100);
  };

  const hasResults =
    filteredCompanies.length > 0 ||
    filteredCarriers.length > 0 ||
    filteredContacts.length > 0 ||
    filteredWorkOrders.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} commandProps={{ shouldFilter: false }}>
      <CommandInput
        placeholder="Search companies, carriers, contacts, work orders..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-[420px]">
        {!q && (
          <>
            <CommandGroup heading="Quick Actions">
              {onCreateWorkOrder && (
                <CommandItem onSelect={() => runAction(onCreateWorkOrder)}>
                  <Plus className="mr-2 h-4 w-4 text-blue-600" />
                  Create Work Order
                </CommandItem>
              )}
              {onCreateCompany && (
                <CommandItem onSelect={() => runAction(onCreateCompany)}>
                  <Plus className="mr-2 h-4 w-4 text-green-600" />
                  Create Company
                </CommandItem>
              )}
              {onCreateCarrier && (
                <CommandItem onSelect={() => runAction(onCreateCarrier)}>
                  <Plus className="mr-2 h-4 w-4 text-orange-600" />
                  Create Carrier
                </CommandItem>
              )}
              {onCreateContact && (
                <CommandItem onSelect={() => runAction(onCreateContact)}>
                  <Plus className="mr-2 h-4 w-4 text-purple-600" />
                  Create Contact
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {q && !hasResults && filteredNav.length === 0 && (
          <CommandEmpty>No results found for &ldquo;{search}&rdquo;</CommandEmpty>
        )}

        {filteredNav.length > 0 && (
          <CommandGroup heading="Pages">
            {filteredNav.map((item) => (
              <CommandItem key={item.href} onSelect={() => navigate(item.href)}>
                <item.icon className="mr-2 h-4 w-4 text-gray-500" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredCompanies.length > 0 && (
          <CommandGroup heading="Companies">
            {filteredCompanies.map((company) => (
              <CommandItem
                key={`company-${company.id}`}
                onSelect={() => navigate(`/companies/${company.id}`)}
              >
                <Building2 className="mr-2 h-4 w-4 text-blue-500" />
                <span className="flex-1 truncate">{company.name}</span>
                {company.email && (
                  <span className="ml-2 text-xs text-muted-foreground truncate max-w-[140px]">
                    {company.email}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredCarriers.length > 0 && (
          <CommandGroup heading="Carriers">
            {filteredCarriers.map((carrier) => (
              <CommandItem
                key={`carrier-${carrier.id}`}
                onSelect={() => navigate(`/carriers/${carrier.id}`)}
              >
                <Truck className="mr-2 h-4 w-4 text-orange-500" />
                <span className="flex-1 truncate">{carrier.name}</span>
                {carrier.docketNumber && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {carrier.docketPrefix}-{carrier.docketNumber}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredContacts.length > 0 && (
          <CommandGroup heading="Contacts">
            {filteredContacts.map((contact) => (
              <CommandItem
                key={`contact-${contact.id}`}
                onSelect={() => navigate(`/contacts/${contact.id}`)}
              >
                <Phone className="mr-2 h-4 w-4 text-purple-500" />
                <span className="flex-1 truncate">
                  {contact.firstName} {contact.lastName}
                </span>
                {contact.companies?.[0]?.company?.name && (
                  <span className="ml-2 text-xs text-muted-foreground truncate max-w-[120px]">
                    {contact.companies[0].company.name}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredWorkOrders.length > 0 && (
          <CommandGroup heading="Work Orders">
            {filteredWorkOrders.map((wo) => (
              <CommandItem
                key={`wo-${wo.id}`}
                onSelect={() => navigate(`/work-orders/${wo.id}`)}
              >
                <FileText className="mr-2 h-4 w-4 text-green-500" />
                <span className="flex-1 truncate">
                  {wo.workOrderNumber || `WO #${wo.id}`}
                </span>
                {wo.carrierName && (
                  <span className="ml-2 text-xs text-muted-foreground truncate max-w-[120px]">
                    {wo.carrierName}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!q && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Navigation">
              {NAV_ITEMS.map((item) => (
                <CommandItem key={item.href} onSelect={() => navigate(item.href)}>
                  <item.icon className="mr-2 h-4 w-4 text-gray-500" />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
